/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useOrgStore } from '../../store/orgStore';
import { organizationService } from '../../services/api';
import { isFinOpsEnabled } from '../../config/featureFlags';
import { Button, EmptyState, Page, PageHeader, SegmentTabs, Tag } from '../../components/console/ui';
import { UsersIc } from '../../components/console/icons';
import ConfirmModal from '../../components/common/ConfirmModal';
import OrgMembersTab from './OrgMembersTab';
import OrgDomainsTab from './OrgDomainsTab';
import OrgSSOTab from './OrgSSOTab';
import FinopsAccessTab from './FinopsAccessTab';

type Tab = 'general' | 'members' | 'domains' | 'sso' | 'finops';

export default function OrganizationSettings() {
  const { organizations, selectedOrgId, fetchOrganizations, selectOrg } = useOrgStore();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('general');
  const [name, setName] = useState('');
  const [billingEmail, setBillingEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const org = organizations.find((o) => o.id === selectedOrgId);
  const isAdmin = org?.role === 'org_owner' || org?.role === 'org_admin';
  const isOwner = org?.role === 'org_owner';

  useEffect(() => {
    if (org) {
      setName(org.name);
      setBillingEmail(org.billing_email || '');
    }
  }, [org]);

  if (!selectedOrgId || !org) {
    return (
      <Page>
        <PageHeader title="Organisation settings" />
        <EmptyState
          icon={<UsersIc />}
          title="No organisation selected"
          body="Select an organisation from the switcher (or create one) to manage its settings."
        />
      </Page>
    );
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await organizationService.update(org.id, { name, billing_email: billingEmail });
      toast.success('Organisation updated');
      fetchOrganizations();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await organizationService.delete(org.id);
      toast.success('Organisation deleted');
      await selectOrg(null);
      navigate('/');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Delete failed');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const tabs: { key: Tab; label: string; show: boolean }[] = [
    { key: 'general', label: 'General', show: true },
    { key: 'members', label: 'Members', show: true },
    { key: 'domains', label: 'Domains', show: isAdmin },
    { key: 'sso', label: 'SSO', show: isAdmin },
    { key: 'finops', label: 'FinOps Access', show: isAdmin && isFinOpsEnabled() },
  ];

  return (
    <Page>
      <PageHeader
        title={org.name}
        subtitle={
          <>
            Organisation settings · your role <Tag tone="off">{(org.role || 'member').replace(/^org_/, '')}</Tag>
          </>
        }
      />
      <div className="max-w-4xl w-full flex flex-col gap-6">
        <SegmentTabs
          label="Organisation sections"
          value={tab}
          onChange={setTab}
          tabs={tabs.filter((t) => t.show).map((t) => ({ value: t.key, label: t.label }))}
        />

        {tab === 'general' && (
          <div className="space-y-6">
            <section className="ps-card p-5 sm:p-6">
              <h2 className="m-0 mb-4 text-lg font-medium text-ps-text">General</h2>
              <form onSubmit={handleSave} className="space-y-4 max-w-md">
                <div>
                  <label className="ps-label" htmlFor="org-name">Name</label>
                  <input
                    id="org-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={!isAdmin}
                    className="ps-input disabled:opacity-60 disabled:cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="ps-label" htmlFor="org-slug">Slug</label>
                  <input id="org-slug" value={org.slug} disabled className="ps-input font-mono opacity-70 cursor-not-allowed" />
                </div>
                <div>
                  <label className="ps-label" htmlFor="org-billing">Billing email</label>
                  <input
                    id="org-billing"
                    type="email"
                    value={billingEmail}
                    onChange={(e) => setBillingEmail(e.target.value)}
                    disabled={!isAdmin}
                    className="ps-input disabled:opacity-60 disabled:cursor-not-allowed"
                  />
                </div>
                {isAdmin && (
                  <Button type="submit" disabled={saving}>
                    {saving ? 'Saving…' : 'Save changes'}
                  </Button>
                )}
              </form>
            </section>

            {isOwner && (
              <section className="ps-card p-5 sm:p-6 !border-red-900/60">
                <h2 className="m-0 mb-2 text-sm font-semibold text-ps-err">Danger zone</h2>
                <p className="m-0 mb-4 text-sm text-ps-muted">
                  Deleting this organisation removes all members, domains and the SSO connection. This cannot be undone.
                </p>
                <Button variant="danger" onClick={() => setShowDeleteConfirm(true)}>
                  Delete organisation
                </Button>
              </section>
            )}
          </div>
        )}
        {tab === 'members' && <OrgMembersTab org={org} isAdmin={isAdmin} isOwner={isOwner} />}
        {tab === 'domains' && isAdmin && <OrgDomainsTab orgId={org.id} />}
        {tab === 'sso' && isAdmin && <OrgSSOTab orgId={org.id} />}
        {tab === 'finops' && isAdmin && isFinOpsEnabled() && <FinopsAccessTab org={org} />}
      </div>

      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        isLoading={deleting}
        title="Delete organisation"
        message={`Delete organisation "${org.name}"? This cannot be undone.`}
        confirmText="Delete organisation"
        variant="danger"
      />
    </Page>
  );
}
